import React, { useState, useEffect } from 'react'
import { Trophy } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'

/**
 * BadgeNotification Component
 * Shows animated badge notifications when users earn new badges
 */
const BadgeNotification = ({ badges = [], onDismiss }) => {
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (badges.length > 0) {
      setVisible(true)
      setCurrentBadgeIndex(0)
    }
  }, [badges])

  const currentBadge = badges[currentBadgeIndex]

  const handleNext = () => {
    if (currentBadgeIndex < badges.length - 1) {
      setCurrentBadgeIndex(currentBadgeIndex + 1)
    } else {
      setVisible(false)
      if (onDismiss) onDismiss()
    }
  }

  const handleDismiss = () => {
    setVisible(false)
    if (onDismiss) onDismiss()
  }

  if (!visible || !currentBadge) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.5 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        onClick={handleDismiss}
      >
        <motion.div
          className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          initial={{ scale: 0.8, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Badge Icon */}
          <motion.div
            className="text-6xl mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          >
            {currentBadge.icon}
          </motion.div>

          {/* Badge Title */}
          <motion.h2
            className="text-2xl font-bold text-gray-900 mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Badge Earned!
          </motion.h2>

          {/* Badge Name */}
          <motion.h3
            className="text-xl font-semibold text-orange-600 mb-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            {currentBadge.name}
          </motion.h3>

          {/* Badge Description */}
          <motion.p
            className="text-gray-600 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            {currentBadge.description}
          </motion.p>

          {/* Progress Indicator */}
          {badges.length > 1 && (
            <motion.div
              className="flex justify-center mb-4 space-x-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              {badges.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentBadgeIndex ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            className="flex gap-3 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            {badges.length > 1 && currentBadgeIndex < badges.length - 1 ? (
              <>
                <button
                  onClick={handleDismiss}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Skip All
                </button>
                <button
                  onClick={handleNext}
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                >
                  Next Badge
                </button>
              </>
            ) : (
              <button
                onClick={handleNext}
                className="px-8 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
              >
                Awesome!
              </button>
            )}
          </motion.div>

          {/* Badge Count */}
          {badges.length > 1 && (
            <motion.p
              className="text-sm text-gray-500 mt-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              {currentBadgeIndex + 1} of {badges.length} badges
            </motion.p>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

/**
 * Hook to show badge notifications using toast
 * For simpler badge notifications without modal
 */
export const useBadgeToast = () => {
  const showBadgeToast = (badge) => {
    toast.custom(
      (t) => (
        <motion.div
          className="bg-white rounded-lg p-4 shadow-lg border border-orange-200 max-w-sm"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
        >
          <div className="flex items-center gap-3">
            <div className="text-2xl">{badge.icon}</div>
            <div className="flex-1">
              <div className="font-bold text-orange-600 text-sm">Badge Earned!</div>
              <div className="font-medium text-gray-900">{badge.name}</div>
              <div className="text-xs text-gray-600">{badge.description}</div>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        </motion.div>
      ),
      {
        duration: 5000,
        position: 'top-right',
      }
    )
  }

  const showBadgeToasts = (badges) => {
    badges.forEach((badge, index) => {
      setTimeout(() => {
        showBadgeToast(badge)
      }, index * 1000) // Stagger by 1 second each
    })
  }

  return { showBadgeToast, showBadgeToasts }
}

export default BadgeNotification